import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { EPlan } from '@shared/types/enums'
import { getInstitutionPackageById, getInstitutionCertificateById, getInstitutionDegreeById, convertFirestoreTimestamp } from '@/components/utils/functions'
import { IDegreePackage, ICertificatePackage } from '@shared/types/package'
import JSZip from 'jszip'
import { ref } from 'vue'
import { IDegree, ICertificate, TransactionData, BitacoraData } from '@shared/types'
import { collection, getDocs, query, Timestamp, where } from 'firebase/firestore'
import { dbRegistros } from '@/firebase/config'
import { formatPrice, formatNumber } from './functions'
const packagesDegrees = ref<IDegreePackage[]>([])
const packagesCertificates = ref<ICertificatePackage[]>([])
const certificatesList = ref<ICertificate[]>([])
const degreesList = ref<IDegree[]>([])
/**
 * @description Constantes para la generación del PDF
 */
const PDF_CONFIG = {
	logoPath: '../../../assets/logo/logoFull.png',
	marginLeft: 20,
	marginTop: 20,
	imageWidth: 80,
	imageHeight: 50,
	tableStyles: {
		fontSize: 8,
		font: 'helvetica',
		textColor: [0, 0, 0] as [number, number, number],
		cellPadding: 3,
		overflow: 'linebreak' as const,
		cellWidth: 'wrap' as const
	},
	headStyles: {
		fillColor: [37, 99, 235] as [number, number, number],
		textColor: [255, 255, 255] as [number, number, number],
		fontStyle: 'bold' as const,
		fontSize: 9,
		halign: 'center' as const
	},
	alternateRowStyles: {
		fillColor: [245, 247, 250] as [number, number, number]
	},
	tableLineColor: [0, 0, 0] as [number, number, number],
	tableLineWidth: 0.1
}

/**
 * @description Obtiene y ordena las transacciones de la institución
 */
const obtenerTransacciones = async (idInstitution: string, mes: { start: Date; end: Date }): Promise<TransactionData[]> => {
	console.log(mes)
	const transaccionesRef = collection(dbRegistros, 'transacciones')
	const inicioMes = new Date(mes.start)
	const finMes = new Date(mes.end)
	console.log(inicioMes, finMes)
	const inicioTimestamp = Timestamp.fromDate(inicioMes)
	const finTimestamp = Timestamp.fromDate(finMes)

	const q = query(transaccionesRef, where('idInstitution', '==', idInstitution), where('dateModification', '>=', inicioTimestamp), where('dateModification', '<=', finTimestamp))
	const transaccionesSnapshot = await getDocs(q)

	if (transaccionesSnapshot.empty) {
		throw new Error('No hay transacciones para generar el acuse')
	}

	const transacciones: TransactionData[] = []
	for (const doc of transaccionesSnapshot.docs) {
		transacciones.push(doc.data() as TransactionData)
	}

	return transacciones.sort((a, b) => (b.dateModification || 0) - (a.dateModification || 0))
}

/**
 * @description Calcula los totales de documentos y monto
 */
const calcularTotales = (transacciones: TransactionData[], precioPorDocumento: number): { totalDecrementos: number; totalAumentos: number; totalMonto: number } => {
	let totalDecrementos = 0
	let totalAumentos = 0
	let totalMonto = 0
	for (const trans of transacciones) {
		const cantidad = trans.quantityDocuments || 0
		const precio = trans.pricePerDocument || precioPorDocumento
		if (
			trans.typeTransaction === 'decremento' &&
			trans.quantityDocuments !== 0 &&
			trans.quantityDocuments !== undefined &&
			trans.quantityDocuments !== null &&
			trans.packageId !== undefined &&
			trans.packageId !== null &&
			trans.packageId !== ''
		) {
			totalDecrementos += cantidad
			totalMonto += cantidad * precio
		} else if (trans.typeTransaction === 'aumento' && trans.quantityDocuments !== 0 && trans.quantityDocuments !== undefined && trans.quantityDocuments !== null) {
			totalAumentos += cantidad
		}
	}

	return { totalDecrementos, totalAumentos, totalMonto }
}

/**
 * @description Filtra las transacciones por tipo
 */
const filtrarTransacciones = (transacciones: TransactionData[]) => {
	const transaccionesDecrementos = transacciones.filter(
		trans =>
			trans.quantityDocuments !== 0 && trans.quantityDocuments !== undefined && trans.quantityDocuments !== null && trans.packageId !== undefined && trans.packageId !== null && trans.packageId !== ''
	)

	const transaccionesAumentos = transacciones.filter(
		trans => trans.quantityDocuments !== 0 && trans.quantityDocuments !== undefined && trans.quantityDocuments !== null && trans.typeTransaction === 'aumento'
	)

	return { transaccionesDecrementos, transaccionesAumentos }
}

/**
 * @description Formatea una fecha para mostrar en el PDF
 */
const formatearFecha = (timestamp: any): string => {
	if (!timestamp) return 'N/A'
	const fecha = convertFirestoreTimestamp(timestamp)
	return new Date(fecha).toLocaleDateString('es-MX', {
		year: 'numeric',
		month: '2-digit',
		day: '2-digit'
	})
}
/**
 * @description Crea el documento PDF base
 */
const crearPDF = (): { doc: jsPDF; yPosition: number; pageWidth: number; checkNewPage: (requiredSpace: number) => boolean } => {
	const doc = new jsPDF({
		orientation: 'portrait',
		unit: 'mm',
		format: 'letter'
	})

	const pageWidth = doc.internal.pageSize.getWidth()
	const pageHeight = doc.internal.pageSize.getHeight()
	let yPosition = PDF_CONFIG.marginTop

	const checkNewPage = (requiredSpace: number): boolean => {
		if (yPosition + requiredSpace > pageHeight - PDF_CONFIG.marginTop) {
			doc.addPage()
			yPosition = PDF_CONFIG.marginTop
			return true
		}
		return false
	}

	return { doc, yPosition, pageWidth, checkNewPage }
}

/**
 * @description Agrega el encabezado al PDF
 */
const agregarEncabezadoPDF = (doc: jsPDF, yPosition: { value: number }, pageWidth: number, institutionName: string): void => {
	// Logo centrado
	const imageX = (pageWidth - PDF_CONFIG.imageWidth) / 2
	doc.addImage(PDF_CONFIG.logoPath, 'PNG', imageX, yPosition.value, PDF_CONFIG.imageWidth, PDF_CONFIG.imageHeight)
	yPosition.value += PDF_CONFIG.imageHeight + 10

	// Título
	doc.setFontSize(20)
	doc.setFont('helvetica', 'bold')
	doc.text('ACUSE DE CONSUMO DE DOCUMENTOS', pageWidth / 2, yPosition.value, { align: 'center' })
	yPosition.value += 10

	// Información de la institución
	doc.setFontSize(12)
	doc.setFont('helvetica', 'normal')
	doc.text(`Institución: ${institutionName}`, PDF_CONFIG.marginLeft, yPosition.value)
	yPosition.value += 6
	doc.text(
		`Fecha de emisión: ${new Date().toLocaleDateString('es-MX', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		})}`,
		PDF_CONFIG.marginLeft,
		yPosition.value
	)
	yPosition.value += 10

	// Línea separadora
	doc.setLineWidth(0.5)
	doc.line(PDF_CONFIG.marginLeft, yPosition.value, pageWidth - PDF_CONFIG.marginLeft, yPosition.value)
	yPosition.value += 8
}

/**
 * @description Agrega la tabla de transacciones para modo postpago
 */
const agregarTablaPostpago = (
	doc: jsPDF,
	yPosition: { value: number },
	transaccionesDecrementos: TransactionData[],
	precioPorDocumento: number,
	totalDocumentos: number,
	totalMonto: number,
	pageWidth: number,
	initalDate: Date,
	finalDate: Date
): void => {
	const tableData = transaccionesDecrementos.map(trans => {
		const fechaFormateada = formatearFecha(trans.dateModification)
		const ambiente = trans.environment === 'production' ? 'Producción' : 'Pruebas'
		const cantidad = trans.quantityDocuments || 0
		const precioUnit = trans.pricePerDocument || precioPorDocumento
		const subtotal = cantidad * precioUnit
		const detalles = trans.details || 'Sin detalles'

		return [fechaFormateada, ambiente, cantidad.toString(), formatPrice(precioUnit).replace('$', ''), formatPrice(subtotal).replace('$', ''), detalles]
	})

	autoTable(doc, {
		head: [['Fecha', 'Ambiente', 'Cant.', 'Precio Unit.', 'Subtotal', 'Detalles']],
		body: tableData,
		startY: yPosition.value,
		margin: { left: PDF_CONFIG.marginLeft, right: PDF_CONFIG.marginLeft },
		styles: PDF_CONFIG.tableStyles,
		headStyles: PDF_CONFIG.headStyles,
		columnStyles: {
			0: { cellWidth: 22, halign: 'center' },
			1: { cellWidth: 25, halign: 'center' },
			2: { cellWidth: 20, halign: 'center' },
			3: { cellWidth: 28, halign: 'right' },
			4: { cellWidth: 28, halign: 'right' },
			5: { cellWidth: 'auto', halign: 'left' }
		},
		alternateRowStyles: PDF_CONFIG.alternateRowStyles,
		tableLineColor: PDF_CONFIG.tableLineColor,
		tableLineWidth: PDF_CONFIG.tableLineWidth
	})

	yPosition.value = (doc as any).lastAutoTable.finalY + 10

	// Totales
	doc.setFontSize(12)
	doc.setFont('helvetica', 'bold')
	doc.text(`Total de documentos consumidos: ${formatNumber(totalDocumentos)}`, PDF_CONFIG.marginLeft, yPosition.value)
	yPosition.value += 4
	doc.setFontSize(10)
	doc.text(`SUBTOTAL: ${formatPrice(totalMonto)}`, pageWidth - PDF_CONFIG.marginLeft, yPosition.value, { align: 'right' })
	yPosition.value += 4
	doc.setFontSize(10)
	doc.text(`IVA: ${formatPrice(totalMonto * 0.16)}`, pageWidth - PDF_CONFIG.marginLeft, yPosition.value, { align: 'right' })
	yPosition.value += 4
	doc.setFontSize(10)
	doc.text(`TOTAL A COBRAR: ${formatPrice(totalMonto + totalMonto * 0.16)}`, pageWidth - PDF_CONFIG.marginLeft, yPosition.value, { align: 'right' })
	yPosition.value += 4
	doc.text(
		`Documentos emitidos entre ${initalDate.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })} y ${finalDate.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}`,
		PDF_CONFIG.marginLeft,
		yPosition.value
	)
	yPosition.value += 10
}

/**
 * @description Agrega las tablas de transacciones para modo prepago
 */
const agregarTablaPrepago = (
	doc: jsPDF,
	yPosition: { value: number },
	tipoPago: EPlan,
	transaccionesDecrementos: TransactionData[],
	transaccionesAumentos: TransactionData[],
	precioPorDocumento: number,
	totalDecrementos: number,
	totalAumentos: number,
	pageWidth: number,
	initalDate: Date,
	finalDate: Date
): void => {
	// Tabla de decrementos
	const tableDataDecrementos = transaccionesDecrementos.map(trans => {
		const fechaFormateada = formatearFecha(trans.dateModification)
		const ambiente = trans.environment === 'production' ? 'Producción' : 'Pruebas'
		const cantidad = trans.quantityDocuments || 0
		const firmadoPor = trans.userId || 'Sin firmar'
		const detalles = trans.details || 'Sin detalles'

		return [fechaFormateada, ambiente, cantidad.toString(), firmadoPor, detalles]
	})

	doc.setFontSize(9)
	doc.setFont('helvetica', 'italic')
	doc.text(
		`Créditos consumidos del ${initalDate.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })} al ${finalDate.toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' })}`,
		PDF_CONFIG.marginLeft,
		yPosition.value
	)
	yPosition.value += 5

	autoTable(doc, {
		head: [['Fecha', 'Ambiente', 'Cant.', 'Firmado por', 'Detalles']],
		body: tableDataDecrementos,
		startY: yPosition.value,
		margin: { left: PDF_CONFIG.marginLeft, right: PDF_CONFIG.marginLeft },
		styles: PDF_CONFIG.tableStyles,
		headStyles: PDF_CONFIG.headStyles,
		columnStyles: {
			0: { cellWidth: 22, halign: 'center' },
			1: { cellWidth: 25, halign: 'center' },
			2: { cellWidth: 20, halign: 'center' },
			3: { cellWidth: 32, halign: 'left' },
			4: { cellWidth: 'auto', halign: 'left' }
		},
		alternateRowStyles: PDF_CONFIG.alternateRowStyles,
		tableLineColor: PDF_CONFIG.tableLineColor,
		tableLineWidth: PDF_CONFIG.tableLineWidth
	})

	yPosition.value = (doc as any).lastAutoTable.finalY + 10

	// Totales de decrementos
	doc.setFontSize(12)
	doc.setFont('helvetica', 'bold')
	doc.text(`Total de documentos consumidos: ${formatNumber(totalDecrementos)}`, PDF_CONFIG.marginLeft, yPosition.value)
	yPosition.value += 8

	// Línea separadora
	doc.setLineWidth(0.5)
	doc.line(PDF_CONFIG.marginLeft, yPosition.value, pageWidth - PDF_CONFIG.marginLeft, yPosition.value)
	yPosition.value += 8

	// Tabla de aumentos
	if (tipoPago === EPlan.PREPAID) {
		const tableDataAumentos = transaccionesAumentos.map(trans => {
			const fechaFormateada = formatearFecha(trans.dateModification)
			const cantidad = trans.quantityDocuments || 0
			const precioUnit = trans.pricePerDocument || precioPorDocumento
			const detalles = trans.details || 'Sin detalles'

			return [fechaFormateada, cantidad.toString(), formatPrice(precioUnit).replace('$', ''), detalles]
		})

		doc.setFontSize(9)
		doc.setFont('helvetica', 'italic')
		doc.text('Créditos adquiridos', PDF_CONFIG.marginLeft, yPosition.value)
		yPosition.value += 5

		autoTable(doc, {
			head: [['Fecha', 'Cant.', 'Precio Unit.', 'Detalles']],
			body: tableDataAumentos,
			startY: yPosition.value,
			margin: { left: PDF_CONFIG.marginLeft, right: PDF_CONFIG.marginLeft },
			styles: PDF_CONFIG.tableStyles,
			headStyles: {
				...PDF_CONFIG.headStyles,
				fillColor: [34, 197, 94] as [number, number, number]
			},
			columnStyles: {
				0: { cellWidth: 22, halign: 'center' },
				1: { cellWidth: 20, halign: 'center' },
				2: { cellWidth: 28, halign: 'right' },
				3: { cellWidth: 'auto', halign: 'left' }
			},
			alternateRowStyles: PDF_CONFIG.alternateRowStyles,
			tableLineColor: PDF_CONFIG.tableLineColor,
			tableLineWidth: PDF_CONFIG.tableLineWidth
		})

		yPosition.value = (doc as any).lastAutoTable.finalY + 10

		// Totales de aumentos
		doc.setFontSize(12)
		doc.setFont('helvetica', 'bold')
		doc.text(`Total de documentos adquiridos: ${formatNumber(totalAumentos)}`, PDF_CONFIG.marginLeft, yPosition.value)
		yPosition.value += 8
	}
}

/**
 * @description Agrega el pie de página al PDF
 */
const agregarPiePaginaPDF = (doc: jsPDF, yPosition: { value: number }, checkNewPage: (requiredSpace: number) => boolean, precioPorDocumento: number): void => {
	checkNewPage(20)
	doc.setFontSize(9)
	doc.setFont('helvetica', 'italic')
	doc.text('Este documento es un acuse de consumo de documentos electrónicos.', PDF_CONFIG.marginLeft, yPosition.value)
	yPosition.value += 5
	doc.text('El monto total corresponde a la suma de todos los creditos consumidos registrados,', PDF_CONFIG.marginLeft, yPosition.value)
	yPosition.value += 5
	doc.text('calculado con el precio por documento de ese momento.', PDF_CONFIG.marginLeft, yPosition.value)
	yPosition.value += 5
	doc.text(`Precio por documento actualmente: ${formatPrice(precioPorDocumento)} más IVA`, PDF_CONFIG.marginLeft, yPosition.value)
}

const generateTemplate = async (): Promise<Uint8Array | null> => {
	try {
		const XLSX = await import('xlsx')

		// Encabezados del Excel
		const headers = ['Nombre del paquete', 'Id del paquete', 'Tipo de documento', 'Folio Control', 'Nombre alumno ', 'Fecha']
		const rows: any[][] = [headers]

		// Función auxiliar para formatear fecha
		const formatDate = (date: number | string | undefined): string => {
			if (!date) return ''
			const dateObj = new Date(date)
			return dateObj.toLocaleDateString('es-MX', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit'
			})
		}
		// Agregar certificados de producción
		for (const certificate of certificatesList.value) {
			const nombrePaquete = packagesCertificates.value.find(paquete => paquete.id === certificate.package)?.label || ''
			rows.push([
				nombrePaquete,
				certificate.package.toString() || '',
				'Certificado',
				certificate.folioControl || '',
				certificate.data?.Alumno?.nombre + ' ' + certificate.data?.Alumno?.primerApellido + ' ' + certificate.data?.Alumno?.segundoApellido || '',
				formatDate(certificate.package)
			])
		}

		// Agregar títulos de producción
		for (const degree of degreesList.value) {
			const nombrePaquete = packagesDegrees.value.find(paquete => paquete.id === degree.package)?.label || ''
			rows.push([
				nombrePaquete,
				degree.package.toString() || '',
				'Título',
				degree.folioControl || '',
				degree.data?.Profesionista?.nombre + ' ' + degree.data?.Profesionista?.primerApellido + ' ' + degree.data?.Profesionista?.segundoApellido || '',
				formatDate(degree.package)
			])
		}

		// Crear el archivo Excel
		const ws = XLSX.utils.aoa_to_sheet(rows)
		const wb = XLSX.utils.book_new()
		XLSX.utils.book_append_sheet(wb, ws, 'Detalles de paquetes firmados')

		// Generar Excel como ArrayBuffer en lugar de descargarlo
		const excelBuffer = XLSX.write(wb, { type: 'array', bookType: 'xlsx' })
		return excelBuffer
	} catch (error) {
		console.error('Error al generar template Excel:', error)
		return null
	}
}
export const descargarAcuseConsumo = async (plan: EPlan, currentInstitution: string, bitacora: BitacoraData | null, mes: { start: Date; end: Date }) => {
	try {
		if (!currentInstitution || !bitacora) {
			console.error('No hay institución seleccionada o datos de bitácora')
			return
		}

		// Obtener y procesar transacciones
		const transacciones = await obtenerTransacciones(currentInstitution, mes)
		const { transaccionesDecrementos, transaccionesAumentos } = filtrarTransacciones(transacciones)
		const { totalDecrementos, totalAumentos, totalMonto } = calcularTotales(transacciones, bitacora.pricePerDocument)

		// Limpiar listas antes de agregar nuevos datos
		degreesList.value = []
		certificatesList.value = []
		packagesDegrees.value = []
		packagesCertificates.value = []

		for (const trans of transaccionesDecrementos) {
			const tipoPaquete = trans.details.includes('Paquete de certificados') ? 'certificates' : 'degrees'
			const packageData = await getInstitutionPackageById(currentInstitution, Number(trans.packageId), tipoPaquete, trans.environment)
			if (packageData) {
				if (tipoPaquete === 'degrees') {
					const degreePromises = Object.keys((packageData as IDegreePackage).degrees)
					for (const degreeId of degreePromises) {
						const degreeData = await getInstitutionDegreeById(currentInstitution, degreeId, trans.environment)
						if (degreeData) {
							degreesList.value.push(degreeData)
						}
					}
					packagesDegrees.value.push(packageData as IDegreePackage)
				} else {
					const certificatePromises = Object.keys((packageData as ICertificatePackage).certificates)
					for (const certificateId of certificatePromises) {
						const certificateData = await getInstitutionCertificateById(currentInstitution, certificateId, trans.environment)
						if (certificateData) {
							certificatesList.value.push(certificateData)
						}
					}
					packagesCertificates.value.push(packageData as ICertificatePackage)
				}
			}
		}
		const { doc, yPosition, pageWidth, checkNewPage } = crearPDF()
		const yPos = { value: yPosition }

		// Agregar encabezado
		agregarEncabezadoPDF(doc, yPos, pageWidth, bitacora.institutionName)

		// Agregar tablas según el tipo de pago
		if (plan === EPlan.POSTPAID) {
			agregarTablaPostpago(doc, yPos, transaccionesDecrementos, bitacora.pricePerDocument, totalDecrementos, totalMonto, pageWidth, mes.start, mes.end)
		} else {
			agregarTablaPrepago(doc, yPos, plan, transaccionesDecrementos, transaccionesAumentos, bitacora.pricePerDocument, totalDecrementos, totalAumentos, pageWidth, mes.start, mes.end)
		}

		// Agregar pie de página
		if (plan !== EPlan.UNLIMITED && plan !== EPlan.BASIC) {
			agregarPiePaginaPDF(doc, yPos, checkNewPage, bitacora.pricePerDocument)
		}

		// Generar Excel en memoria
		const excelBuffer = await generateTemplate()

		// Generar PDF en memoria
		const pdfBlob = doc.output('blob')
		const pdfFileName = `Acuse_Consumo_${bitacora.institutionName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`

		// Crear ZIP con ambos archivos
		const zip = new JSZip()
		zip.file(pdfFileName, pdfBlob)

		if (excelBuffer) {
			const fechaActual = new Date().toISOString().split('T')[0]
			zip.file(`detalles_paquetes_${fechaActual}.xlsx`, excelBuffer)
		}

		// Generar y descargar ZIP
		const zipBlob = await zip.generateAsync({ type: 'blob' })
		const zipUrl = globalThis.URL.createObjectURL(zipBlob)
		const zipLink = document.createElement('a')
		zipLink.href = zipUrl
		zipLink.download = `Acuse_Consumo_${bitacora.institutionName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.zip`
		document.body.appendChild(zipLink)
		zipLink.click()
		globalThis.URL.revokeObjectURL(zipUrl)
		zipLink.remove()
	} catch (err: any) {
		console.error('Error al generar acuse de consumo:', err)
		throw new Error('Error al generar el acuse de consumo: ' + (err.message || 'Error desconocido'))
	}
}
